/**
 * API client — typed fetch wrappers for all Praenum backend endpoints.
 * All functions return parsed JSON or throw an Error with a human-readable message.
 *
 * When VITE_MOCK=true (set in .env.development.local), all calls return mock
 * data so the frontend is fully browsable without a running backend.
 */

import {
  MOCK_CLIENT_ID,
  MOCK_ORG_NAME,
  MOCK_PREFERENCES,
  MOCK_GRANTS,
  MOCK_FUNDER_MATCHES,
  MOCK_FUNDER_PROFILE,
  MOCK_DIGEST_HISTORY,
  MOCK_CORPUS_SEARCH,
  MOCK_COFUNDERS,
  MOCK_ADMIN_CLIENTS,
} from './mockData'
import { clearSession } from './session'

const IS_MOCK = import.meta.env.VITE_MOCK === 'true'
const BASE = '/api'

function delay(ms = 300) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Turn FastAPI's `detail` into one readable sentence.
 *
 * `HTTPException` sends a string, but a **422 from a Pydantic validator sends a
 * LIST** of `{loc, msg, type}` objects. The old code did `data.detail || …`,
 * which handed that array to `new Error(...)` and produced the literal text
 * "[object Object]" on screen.
 *
 * That was invisible while every validator-backed field lived on Preferences
 * behind a Save button, and stops being invisible now: the EIN prompt asks a
 * client to type nine digits at the exact moment we are asking them to trust
 * us, and the FIRST thing a typo earned them was "[object Object]". The
 * validator's own message is genuinely good — "An EIN is 9 digits, like
 * 04-2121342. Leave it blank if you'd rather not provide it." — it just never
 * reached anyone.
 *
 * Pydantic v2 prefixes value-error messages with "Value error, "; that is
 * internal vocabulary, so it is stripped.
 */
function detailMessage(detail) {
  if (!detail) return null
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    // First error only. A field-by-field list is the right shape for a big
    // form; every caller here submits one or two fields, so the first message
    // IS the problem and a joined list just reads as noise.
    const msg = detail.find(d => d && d.msg)?.msg
    return msg ? String(msg).replace(/^Value error,\s*/, '') : null
  }
  return null
}

async function request(method, path, body = null) {
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json' },
    // Send the HttpOnly auth cookie on every request (same-origin). The cookie,
    // not any header, is what authorizes the call.
    credentials: 'include',
  }
  if (body !== null) {
    opts.body = JSON.stringify(body)
  }

  const res = await fetch(`${BASE}${path}`, opts)

  if (!res.ok) {
    // A 401 means the auth cookie is missing/expired/invalid — clear the cached
    // client summary so RequireAuth bounces the user back to sign-in.
    if (res.status === 401) clearSession()
    let message = `Request failed (${res.status})`
    try {
      const data = await res.json()
      message = detailMessage(data.detail) || data.message || message
    } catch (_) {}
    const err = new Error(message)
    // 402 = the session is valid but the plan has ended. Distinct from 401 on
    // purpose: the user stays signed in and can still reach billing, so callers
    // render a resubscribe state rather than bouncing them to sign-in.
    if (res.status === 402) err.planEnded = true
    err.status = res.status
    throw err
  }

  return res.json()
}

// ── Public config + beta invites ──────────────────────────────────────────────

// Runtime flags the SPA needs before login. Currently just whether public
// signup is open (false during the private beta). Read on app load so the UI
// can't drift from the backend flag.
export async function getPublicConfig() {
  if (IS_MOCK) {
    await delay(50)
    return { public_signup: true }
  }
  return request('GET', '/public-config')
}

// Validate a beta invite token (read-only). Resolves to { email, plan_tier }
// for a valid invite, or throws (404) for unknown / expired / already-used.
export async function getBetaInvite(token) {
  if (IS_MOCK) {
    await delay()
    return { email: 'beta-tester@nonprofit.org', plan_tier: 'pro' }
  }
  return request('GET', `/beta-invite/${encodeURIComponent(token)}`)
}

// ── Onboarding ────────────────────────────────────────────────────────────────

export async function onboard(payload) {
  if (IS_MOCK) {
    await delay()
    return {
      client_id: MOCK_CLIENT_ID,
      trial_ends_at: new Date(Date.now() + 14 * 86400000).toISOString(),
      message: '[Mock] Welcome! Your 14-day free trial has started.',
    }
  }
  // The backend sets an HttpOnly auth cookie on this response.
  return request('POST', '/onboard', payload)
}

// "Is this you?" — resolve an org name to an EIN against our own 990 grant data
// (#62).
//
// ⚠️ NOT public. Requires a signed-in client with a CONFIRMED email (ADR-0018),
// so this 403s for an account that has not clicked its confirmation link — which
// `get_current_client` still admits to the dashboard. Callers must degrade to
// the typed EIN field rather than surfacing that error. The only caller is
// OrgIdentitySetup, on the dashboard, after confirmation (#65).
//
// Resolves to { outcome: 'match' | 'choose' | 'none', suggestions: [...] }. The
// caller must let the human confirm — never write the EIN on its own, and never
// preselect when `outcome` is 'choose'.
export async function lookupOrg(name, state) {
  if (IS_MOCK) {
    await delay(200)
    if (/ywca/i.test(name)) {
      return {
        outcome: 'choose',
        max_choices: 5,
        suggestions: [
          {
            name: 'YWCA of Lowell', city: 'Lowell', state: 'MA',
            ein: '04-2103580', ein_url: '#', funder_count: 6,
            evidence_is_local: true,
          },
          {
            name: 'New Bedford YWCA', city: 'New Bedford', state: 'MA',
            ein: '04-6001234', ein_url: '#', funder_count: 2,
            evidence_is_local: true,
          },
        ],
      }
    }
    if (/green valley/i.test(name)) {
      return {
        outcome: 'match',
        max_choices: 5,
        suggestions: [{
          name: MOCK_ORG_NAME, city: 'Halifax', state: state || 'NC',
          ein: '56-1234567', ein_url: '#', funder_count: 4,
          evidence_is_local: true,
        }],
      }
    }
    return { outcome: 'none', max_choices: 5, suggestions: [] }
  }
  const params = new URLSearchParams({ name, state })
  return request('GET', `/org-lookup?${params}`)
}

// ── Authentication ──────────────────────────────────────────────────────────
// login / resetPassword set the HttpOnly auth cookie server-side and return
// only the (non-secret) client summary in the body.

const MOCK_CLIENT_SUMMARY = {
  client: {
    client_id: MOCK_CLIENT_ID,
    email: 'director@greenvalley.org',
    org_name: MOCK_ORG_NAME,
    plan_tier: 'growth',
    plan_status: 'trial',
    email_confirmed: true,
  },
}

export async function login(email, password) {
  if (IS_MOCK) {
    await delay()
    return { client: { ...MOCK_CLIENT_SUMMARY.client, email } }
  }
  return request('POST', '/login', { email, password })
}

export async function logout() {
  if (IS_MOCK) {
    await delay(100)
    return { message: '[Mock] Signed out.' }
  }
  return request('POST', '/logout')
}

export async function getMe() {
  if (IS_MOCK) {
    await delay(100)
    return MOCK_CLIENT_SUMMARY.client
  }
  return request('GET', '/me')
}

export async function forgotPassword(email) {
  if (IS_MOCK) {
    await delay()
    return { message: '[Mock] If an account exists, a reset link has been sent.' }
  }
  return request('POST', '/forgot-password', { email })
}

export async function resetPassword(token, newPassword) {
  if (IS_MOCK) {
    await delay()
    return { ...MOCK_CLIENT_SUMMARY }
  }
  return request('POST', '/reset-password', { token, new_password: newPassword })
}

// ── Preferences ───────────────────────────────────────────────────────────────
// Auth-gated: the session cookie identifies the client server-side — no id in
// the path (the old /preferences/{client_id} form was unauthenticated).

export async function getPreferences() {
  if (IS_MOCK) {
    await delay()
    return { ...MOCK_PREFERENCES }
  }
  return request('GET', '/preferences')
}

export async function updatePreferences(updates) {
  if (IS_MOCK) {
    await delay()
    // Return merged preferences so the UI reflects the change
    return { ...MOCK_PREFERENCES, ...updates }
  }
  return request('PATCH', '/preferences', updates)
}

// ── Feedback ──────────────────────────────────────────────────────────────────

export async function recordFeedback(payload) {
  if (IS_MOCK) {
    await delay(150)
    return { recorded: true, message: '[Mock] Feedback recorded.' }
  }
  return request('POST', '/feedback', payload)
}

// Thumbs up/down on a foundation lead (payload: client_id, funder_id, feedback).
export async function recordFunderFeedback(payload) {
  if (IS_MOCK) {
    await delay(150)
    return { recorded: true, message: '[Mock] Feedback recorded.' }
  }
  return request('POST', '/funder-feedback', payload)
}

// ── Email confirmation ────────────────────────────────────────────────────────

export async function resendConfirmation(email) {
  if (IS_MOCK) {
    await delay()
    return { sent: true, message: '[Mock] Confirmation email resent.' }
  }
  return request('POST', '/resend-confirmation', { email })
}

// ── Unsubscribe / resubscribe ─────────────────────────────────────────────────
// unsubscribe() is called from the public /unsubscribe page (no login — the
// client_id comes from the digest email link). resubscribe() is auth-gated.

export async function unsubscribe(clientId) {
  if (IS_MOCK) {
    await delay()
    return { message: '[Mock] You are unsubscribed.' }
  }
  return request('POST', '/unsubscribe', { client_id: clientId })
}

export async function resubscribe() {
  if (IS_MOCK) {
    await delay()
    return { message: '[Mock] Digests re-enabled.' }
  }
  return request('POST', '/resubscribe')
}

// ── Dashboard: matches + digest history ──────────────────────────────────────
// Auth-gated (the cookie identifies the client server-side; no id in the path).

export async function getMatches() {
  if (IS_MOCK) {
    await delay()
    return MOCK_GRANTS
  }
  return request('GET', '/matches')
}

export async function getDigestHistory() {
  if (IS_MOCK) {
    await delay()
    return MOCK_DIGEST_HISTORY
  }
  return request('GET', '/digest-history')
}

// Grant search (Phase 7F). Auth-gated: searches the whole grants corpus with
// a live personalized score per result. `params` may include q, category,
// state, source, status (open|closed|all), sort (newest|closing_soon), page,
// page_size — empty values are omitted from the query string.
export async function searchGrants(params = {}) {
  if (IS_MOCK) {
    await delay()
    const results = MOCK_GRANTS.map(g => ({ ...g, status: 'posted', has_match: true }))
    return { total: results.length, page: 1, page_size: 20, results }
  }
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') qs.set(key, value)
  }
  const query = qs.toString()
  return request('GET', `/grants/search${query ? `?${query}` : ''}`)
}

// Search the client's OWN matched foundations (homelab-kb #60, Phase 1).
// Scope is deliberately their existing matches — this reaches no funder they
// do not already have a FunderMatch for, so it is not corpus search.
export async function searchFunderMatches(params = {}) {
  if (IS_MOCK) {
    await delay()
    return { total: 0, page: 1, page_size: 20, results: [] }
  }
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') qs.set(key, value)
  }
  const query = qs.toString()
  return request('GET', `/funder-matches/search${query ? `?${query}` : ''}`)
}

// Search the WHOLE funder corpus (#60 Phase 2a, ADR-0012). Pro+ only —
// the server 403s for lower tiers, and the page renders that as an upgrade
// prompt rather than an error. Deliberately returns funders the digest would
// never show, each carrying `why_not` to explain its absence.
export async function searchCorpus(params = {}) {
  if (IS_MOCK) {
    await delay()
    return MOCK_CORPUS_SEARCH
  }
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') qs.set(key, value)
  }
  const query = qs.toString()
  return request('GET', `/funders/search${query ? `?${query}` : ''}`)
}

// Foundation leads (Phase 9C). Tier-gated server-side: Growth+ gets
// { has_access: true, funders: [...] }; Starter gets
// { has_access: false, waiting_count: N, funders: [] } for the upgrade teaser.
export async function getFunderMatches() {
  if (IS_MOCK) {
    await delay()
    return MOCK_FUNDER_MATCHES
  }
  return request('GET', '/funder-matches')
}

// The "funders to cultivate" lane (ADR-0052, #151). A SEPARATE call from
// getFunderMatches, not a parameter on it: these funders have no application,
// so the card is a different shape and the page that renders it makes a
// different promise. `enabled` is false when FUNDER_CULTIVATE_ENABLED is off,
// which the page must distinguish from "we found none for you".
export async function getCultivate() {
  if (IS_MOCK) {
    await delay()
    return { enabled: false, lane_size: 0, cards: [] }
  }
  return request('GET', '/cultivate')
}

// Full profile for ONE foundation lead, including its filed giving history.
// Server-side this 404s for any funder the client has no match for — the
// ranked lead list is the product, so ids are not browsable.
export async function getFunderProfile(funderId) {
  if (IS_MOCK) {
    await delay()
    return MOCK_FUNDER_PROFILE
  }
  return request('GET', `/funders/${funderId}`)
}

// Co-funder leads — "foundations that fund alongside yours" (#61, ADR-0014).
// EVERY paying tier; depth is graded server-side (3/5/8/8) and reported as
// `tier_limit`.
//
// ⚠️ `known`, `in_graph` and an empty `leads` are THREE DIFFERENT ANSWERS and
// callers must not collapse them into one empty state:
//   known:false     → we hold no usable EIN, so we could not look at all
//   in_graph:false  → the EIN is fine but appears in no filing we hold
//   leads:[]        → we found the client's funders; nothing survived the filters
// Only 13% of funders itemize recipient EINs, so "we found nothing" is a gap in
// the IRS data — never evidence that nobody co-funds this client.
export async function getCoFunders() {
  if (IS_MOCK) {
    await delay()
    return MOCK_COFUNDERS
  }
  return request('GET', '/co-funders')
}

// ── Billing: start a Stripe Checkout session ─────────────────────────────────

export async function startCheckout(planTier) {
  if (IS_MOCK) {
    await delay()
    return { checkout_url: 'https://checkout.stripe.com/c/pay/mock_session' }
  }
  return request('POST', '/billing/checkout', { plan_tier: planTier })
}

export async function openBillingPortal() {
  if (IS_MOCK) {
    await delay()
    return { portal_url: 'https://billing.stripe.com/p/session/mock' }
  }
  return request('POST', '/billing/portal')
}

// ── Account self-management ───────────────────────────────────────────────────

export async function changePassword(currentPassword, newPassword) {
  if (IS_MOCK) {
    await delay()
    return { message: '[Mock] Password updated.' }
  }
  return request('POST', '/account/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  })
}

export async function changeEmail(newEmail, currentPassword) {
  if (IS_MOCK) {
    await delay()
    return { message: '[Mock] Email updated. Confirmation sent.' }
  }
  return request('POST', '/account/change-email', {
    new_email: newEmail,
    current_password: currentPassword,
  })
}

// ── Admin console (read-only) ────────────────────────────────────────────────
// Every /api/admin/* route returns 404 — not 403 — when the caller is not an
// operator, so a non-admin cannot tell the surface exists. Callers must treat a
// 404 here as "you are not an admin", never as "the server is broken".

export async function getAdminClients() {
  if (IS_MOCK) {
    await delay()
    return MOCK_ADMIN_CLIENTS
  }
  return request('GET', '/admin/clients')
}

export { IS_MOCK }
